/**
 * Avatar-dropdown "Linked wheels" section (Gordon 2026-07-20; row model
 * corrected per BUG-profile-894a6c, Gordon 2026-08-14).
 *
 * Lists the signed-in user's OWN link-clusters as CONSTELLATION BUNDLES — one
 * row per cluster, never one row per individual wheel (a flat per-wheel list
 * is the wrong model: LinkedWheels is a wheel + its linked wheels, not a
 * wheel directory). Activating a bundle opens its primary wheel's page
 * (/wheel/:id), which is where the full constellation map lives (the map now
 * lives ONLY on a wheel's own page, per Gordon's 2026-07-20 ruling that
 * removed the creator-wide map from the Gallery).
 *
 * Reuses the dropdown's existing Divider / DropdownItem chrome (./styles) so it is
 * byte-consistent with the neighbouring menu rows. Data comes from the same
 * react-query creator-graph hook the (removed) gallery map used, so no new API.
 *
 * Fail-quiet + flag-gated: renders NOTHING unless REACT_APP_LINKED_WHEELS_ENABLED
 * is on (same feature family — no separate sub-flag, so it ships/QA's with the
 * family flag already flipped in staging). It is also hidden while loading, on any
 * error/403/404, and whenever the user has no linked wheels — so a menu never gains
 * a dead/empty section (G-54). Mounted only inside the OPEN logged-in dropdown, so
 * the graph request fires lazily the first time the menu is opened, never on load.
 *
 * TEMP HIDE (Gordon 2026-08-18, ISP-9999): this section doesn't yet do what's
 * intended -- it renders as its own labelled sub-section listing linked wheels,
 * but the real spec is a full "Constellations" sub-page
 * (specs/linked-wheels-constellations/SPEC-2026-08-18.md, EXTREMELY LOW
 * priority, NOT being built here). Hidden below via HIDE_LINKED_WHEELS_SECTION
 * while that spec is unbuilt. All data/logic stays intact -- flip the constant
 * back to false when Constellations ships.
 */
import React from "react";
import PropTypes from "prop-types";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";

import * as Styled from "./styles";
import { colors } from "../../utils";
import { LINKED_WHEELS_ENABLED } from "../LinkedWheels/featureFlag";
import { useCreatorWheelGraph } from "../LinkedWheels/useCreatorWheelGraph";
import { groupLinkedWheels } from "../LinkedWheels/logic";

const LABEL_GREY = colors.StrokePrimary || colors.textGrey || "#667085";

// TEMP HIDE (Gordon 2026-08-18, ISP-9999): see file header. Set to false to
// restore the section once Constellations ships.
const HIDE_LINKED_WHEELS_SECTION = true;

function BundleRow({ group, onActivate }) {
  const primary = group[0];
  const label = primary.name || `Wheel ${primary.id}`;
  const others = group.length - 1;
  const go = () => onActivate(primary.id);
  return (
    <Styled.DropdownItem
      role="button"
      tabIndex={0}
      aria-label={`Open ${label} constellation (${others} linked wheel${
        others === 1 ? "" : "s"
      })`}
      title={label}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          go();
        }
      }}
    >
      {primary.portrait ? (
        <img
          src={primary.portrait}
          alt=""
          aria-hidden="true"
          width={18}
          height={18}
          style={{
            borderRadius: "50%",
            objectFit: "cover",
            marginRight: 10,
            flex: "0 0 auto",
          }}
        />
      ) : (
        <HubOutlinedIcon
          aria-hidden="true"
          style={{ fontSize: 16, marginRight: 10, color: LABEL_GREY, flexShrink: 0 }}
        />
      )}
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: 150,
        }}
      >
        {label} +{others}
      </span>
    </Styled.DropdownItem>
  );
}

BundleRow.propTypes = {
  group: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string,
      portrait: PropTypes.string,
    })
  ).isRequired,
  onActivate: PropTypes.func.isRequired,
};

/**
 * @param {object} props
 * @param {number|string} props.userId  the signed-in user's id
 * @param {(id:number|string)=>void} props.onNavigate  handles a row activation
 */
export default function AvatarLinkedWheelsSection({ userId, onNavigate }) {
  const { data, isLoading, isError } = useCreatorWheelGraph(userId);

  if (HIDE_LINKED_WHEELS_SECTION) return null;
  if (!LINKED_WHEELS_ENABLED) return null;
  if (userId == null) return null;
  if (isLoading || isError || !data) return null;

  const groups = groupLinkedWheels(data);
  if (groups.length === 0) return null;

  return (
    <>
      <Styled.Divider />
      <div
        role="group"
        aria-label="Linked wheels"
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px 2px" }}
      >
        <HubOutlinedIcon
          aria-hidden="true"
          style={{ fontSize: 16, color: colors.PrimaryPurple }}
        />
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: "uppercase",
            color: LABEL_GREY,
          }}
        >
          Linked wheels
        </span>
      </div>
      <div role="list">
        {groups.map((group) => (
          <BundleRow key={`lw-group-${group[0].id}`} group={group} onActivate={onNavigate} />
        ))}
      </div>
    </>
  );
}

AvatarLinkedWheelsSection.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onNavigate: PropTypes.func.isRequired,
};
